import { ReactElement } from 'react';
import {
  type IdsByAppInput,
  type App,
  type Product,
  type Venue,
  type MenuVariant,
  type Menu,
  type Surcharge,
  StatusEnum,
  type MenuOption,
  ProductVariablesByApp,
  DayOfWeekEnum
} from 'generated/graphql';
import { UniqueIdentifier } from '@dnd-kit/core';
import { ApolloError } from '@apollo/client';
import { DAYS_OF_WEEK } from 'consts';

export type NavMenuItem = {
  label: string;
  url: string;
  miniLabel?: string;
  isActive?: boolean;
  icon: string;
  mobileOnly?: boolean;
};

export type OptionLink = {
  type: 'link';
  url: string;
  openNewPage: boolean;
};

export type OptionButton = {
  type: 'button';
  isRedHoverBackground?: boolean;
  callback?: () => void;
};

export type OptionText = {
  type: 'text';
};

export type OptionBase = {
  value: string;
  content?: ReactElement;
  hidden?: boolean;
};

export type Option = OptionBase & (OptionLink | OptionButton | OptionText);

export type Availability = {
  id: string;
  name: string;
  slug: string;
};

export type SocialLink = {
  url: string;
  iconName: string;
};

export type Link = {
  url: string;
  label: string;
};

export type MenuTile = {
  mmId?: string | undefined | null;
  name: string;
  apps: App[];
  neverPublished: boolean;
  busyUntil?: string | null;
  surcharge?: Surcharge | null;
};

export type AvailabilityChangeConfirmProps = {
  type: 'AVAILABILITY_CHANGE_CONFIRM';
  apps: App[];
  menuItem: Product | MenuVariant;
  callback: () => void;
  isAvailable: boolean;
};

export type CancelConfirmProps = {
  type: 'CANCEL_CONFIRM';
  callback: () => void;
};

export type ErrorProps = {
  type: 'ERROR';
  title: string;
  preBodyContent?: JSX.Element | null;
  body: JSX.Element | string;
  buttons: {
    label: string;
    level?: string;
    url?: string;
    callback?: () => void;
  }[];
  canDismiss?: boolean;
  icon?: string;
};

export type EditConfirmProps = {
  type: 'EDIT_CONFIRM';
  item: Product | undefined;
  affectedApps: App[];
  callback: () => Promise<{
    error: ApolloError;
    currentVenue: Venue | undefined | null;
  } | null>;
  hasBrands: boolean;
  venueName?: string;
};

export type DoshiiItemVariantAvailabilityChangeProps = {
  type: 'DOSHII_ITEM_VARIANT_AVAILABILITY_CHANGE';
  callback: () => void;
};

export type InvalidFieldsMenuProps = {
  type: 'INVALID_FIELDS_SINGLE_MENU_PUBLISH';
  items: string[];
  publish: boolean;
};

export type InvalidFieldsSingleItemProps = {
  type: 'INVALID_FIELDS_SINGLE_ITEM';
  item: string;
  affectedApps: App[];
  hasBrands: boolean;
  venueName?: string;
};

export type InvalidFieldsProps = InvalidFieldsSingleItemProps | InvalidFieldsMenuProps;

export type DeletePhotoConfirmProps = {
  type: 'DELETE_PHOTO';
  callback: () => void;
};

export type PublishMenuProps = {
  type: 'PUBLISH_MENU';
  firstPublish: boolean;
  affectedItemsAndOptionsApps: App[];
  affectedApps: App[];
  menu: Menu;
  hasBrands: boolean;
  venueName?: string;
  callback: () => Promise<{
    error: ApolloError;
    currentVenue: Venue | undefined;
  } | null>;
};

export type DOORDASH_WARNING = {
  type: 'DOORDASH_WARNING';
};

export type DOSHII_ITEM_VARIANT_AVAILABILITY_CHANGE = {
  type: 'DOSHII_ITEM_VARIANT_AVAILABILITY_CHANGE';
  callback: () => void;
};

export type OPTIONS_AVAILABILITY_UNSUPPORTED_APP_WARNING = {
  type: 'OPTIONS_AVAILABILITY_UNSUPPORTED_APP_WARNING';
  apps: App[];
};

export type BROKEN_IMAGES = {
  type: 'BROKEN_IMAGES';
};

export type POSDefaultsProps = {
  type: 'POS_DEFAULTS';
  menuItem: Product | null;
};

export type BoostPeakCallToActionProps = {
  type: 'BOOST_PEAK_CALL_TO_ACTION';
  venueId: string;
  isWideModal?: boolean;
};

export type BoostLowCallToActionProps = {
  type: 'BOOST_LOW_CALL_TO_ACTION';
  venueId: string;
  posName: string;
  isWideModal?: boolean;
};

export type CommonHoursConfirmProps = {
  type: 'COMMON_HOURS_CONFIRM';
  callback: () => void;
};

export type ModalData =
  | AvailabilityChangeConfirmProps
  | ErrorProps
  | EditConfirmProps
  | InvalidFieldsProps
  | DeletePhotoConfirmProps
  | POSDefaultsProps
  | DOORDASH_WARNING
  | PublishMenuProps
  | BROKEN_IMAGES
  | OPTIONS_AVAILABILITY_UNSUPPORTED_APP_WARNING
  | DOSHII_ITEM_VARIANT_AVAILABILITY_CHANGE
  | CancelConfirmProps
  | BoostPeakCallToActionProps
  | BoostLowCallToActionProps
  | CommonHoursConfirmProps;

export type ModalType = {
  isVisible: boolean;
  data?: ModalData;
};

export type StickyHeaderData = {
  isVisibleHeight: number;
  primaryAction: {
    text: string | JSX.Element;
    callback: () => void;
  };
  secondaryAction?: {
    text: string | JSX.Element;
    callback: () => void;
  };
  text: string | JSX.Element;
  label: string;
};

export type StickyHeaderType = {
  isVisible: boolean;
  data?: StickyHeaderData;
};

export enum SnackBarTypeEnum {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS'
}

export type SnackBarType = {
  id: string;
  itemMmIdIfDetails?: string;
  text: string;
  type: SnackBarTypeEnum;
  time: number;
  callback?: () => void;
};

export type ReducerActionOptions = {
  key: 'options';
  payload: MenuOption[];
};

export type ReducerState = Product | null;

export type ReducerAction =
  | { key: 'item'; payload: Product }
  | { key: 'imageUri-all'; payload: string | null }
  | { key: 'imageUri-single'; payload: { appId: string; nextValue: string | null } }
  | ReducerActionOptions
  | { key: 'variablesByApp'; payload: ProductVariablesByApp[] }
  | { key: 'description'; payload: string }
  | { key: 'dietary'; payload: string[] }
  | { key: 'name'; payload: string };

export type NestedCategory = {
  __typename?: 'Category';
  category: string;
  description?: string | null;
  displayOrder: number;
  id: string;
  imageUri?: string | null;
  products: Product[];
};

export type formattedCategory = {
  [id: string]: string[];
};

export type Items = Record<UniqueIdentifier, UniqueIdentifier[]>;

export type ReorderMutationType = {
  mmId: string;
  name?: string;
  products: string[];
  idsByApp: IdsByAppInput[];
};

export type QueuedProductImageUpdate = {
  posId: string;
  image: { file: File; docType: string } | null;
  localURL: string | null;
  appIds: string[];
};

export enum OptionSource {
  POS_PRODUCT = 'pos-product',
  APP_GLOBAL = 'app-global'
}

export type DayOfWeekKey = (typeof DAYS_OF_WEEK)[number];

export type Time = {
  from: string;
  to: string;
};

export type DayOfWeekType = {
  day: DayOfWeekKey;
  hours: {
    status: StatusEnum;
    time: Time[];
  };
  error?: string;
};

export type MenuHoursType = {
  [key in DayOfWeekKey]: DayOfWeekType;
};

// TODO: later on, we can use this type to replace the above DayOfWeekType and MenuHoursType
// export type DayOfWeekType<T extends DayOfWeekKey = DayOfWeekKey> = {
//   day: T;
//   hours: {
//     status: StatusEnum;
//     time: Time[];
//   };
// };

// export type MenuHoursType = {
//   [dayOfWeek in DayOfWeekKey]: DayOfWeekType<dayOfWeek>;
// };

export type AllMenuHours = {
  [key: string]: MenuHoursType;
};

export type ClosedDates = {
  from: string;
  to: string;
  isRange: boolean;
  error?: string;
};

export const convertDayOfWeekKeyToDayOfWeekEnum = (value: DayOfWeekKey): DayOfWeekEnum => {
  switch (value) {
    case 'mon':
      return DayOfWeekEnum.Mon;
    case 'tue':
      return DayOfWeekEnum.Tue;
    case 'wed':
      return DayOfWeekEnum.Wed;
    case 'thu':
      return DayOfWeekEnum.Thu;
    case 'fri':
      return DayOfWeekEnum.Fri;
    case 'sat':
      return DayOfWeekEnum.Sat;
    case 'sun':
      return DayOfWeekEnum.Sun;
    default:
      throw new Error(`Invalid day of week: ${value}`);
  }
};
